import { useState } from "react";
import {
  Box, Paper, TextField, InputAdornment, Alert, Stack, Button, Chip, MenuItem,
} from "@mui/material";
import { PageHeader } from "@/components/PageHeader";
import { RowActions } from "@/components/RowActions";
import { GenericInfoButton } from "@/components/GenericInfoButton";
import { DataGrid, type GridColDef, type GridPaginationModel, type GridRowParams } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { itemsApi, type ItemDto } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { useTaxRegime } from "@/shared/hooks/useTaxRegime";
import { ItemForm } from "./ItemForm";

export function ItemsPage() {
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<"active" | "inactive" | "all">("active");
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 });
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ItemDto | null>(null);

  const canCreate = useAuth(s => s.hasPermission("inventory.item.create"));
  const canUpdate = useAuth(s => s.hasPermission("inventory.item.edit"));
  const canDelete = useAuth(s => s.hasPermission("inventory.item.delete"));
  const { taxLabel } = useTaxRegime();
  const qc = useQueryClient();

  const query = useQuery({
    queryKey: ["items", search, status, pagination.page, pagination.pageSize],
    queryFn: () => itemsApi.list({
      search: search || undefined,
      isActive: status === "all" ? undefined : status === "active",
      page: pagination.page + 1, pageSize: pagination.pageSize,
    }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => itemsApi.delete(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["items"] }),
  });

  const openCreate = () => { setEditing(null); setFormOpen(true); };
  const openEdit = (i: ItemDto) => { setEditing(i); setFormOpen(true); };
  const handleDelete = (i: ItemDto) => {
    if (confirm(`Delete item "${i.name}"? Blocked if any inventory movement exists for this item.`)) {
      deleteMutation.mutate(i.id);
    }
  };

  const columns: GridColDef<ItemDto>[] = [
    { field: "sku", headerName: "SKU", width: 120 },
    { field: "name", headerName: "Name", flex: 1, minWidth: 220 },
    // Icon only: the generic name repeats what the item name already says on most rows, so the
    // column earned its width back as a marker — present means "linked to the generic master",
    // and the button opens the monograph. Nothing renders for an unmapped item.
    {
      field: "genericNameId", headerName: "Generic", width: 80, sortable: false, filterable: false,
      align: "center", headerAlign: "center", display: "flex",
      renderCell: (p) => (
        <GenericInfoButton genericNameId={p.row.genericNameId} genericName={p.row.genericName} />
      ),
    },
    { field: "itemType", headerName: "Type", width: 100 },
    { field: "form", headerName: "Form", width: 90 },
    { field: "strength", headerName: "Strength", width: 100 },
    { field: "manufacturer", headerName: "Manufacturer", width: 150 },
    { field: "barcode", headerName: "Barcode", width: 130, valueFormatter: (v) => v || "—" },
    {
      field: "isScheduleDrug", headerName: "Schedule", width: 110,
      renderCell: (p) => p.value
        ? <Chip size="small" color="warning" label={`Schedule ${p.row.scheduleCategory ?? ""}`.trim()} />
        : null,
    },
    {
      field: "gstRate", headerName: `${taxLabel}%`, width: 70, type: "number",
      valueFormatter: (v) => v == null ? "—" : `${v}%`,
    },
    {
      field: "isActive", headerName: "Status", width: 90,
      renderCell: (p) => (
        <Chip size="small" color={p.value === false ? "default" : "success"}
          label={p.value === false ? "Inactive" : "Active"} />
      ),
    },
    {
      field: "actions", headerName: "", width: 96, sortable: false, filterable: false,
      renderCell: (p) => (
        <RowActions actions={[
          { label: "Edit", icon: <EditIcon fontSize="small" />, hidden: !canUpdate, onClick: () => openEdit(p.row) },
          { label: "Delete", icon: <DeleteIcon fontSize="small" />, color: "error", hidden: !canDelete, onClick: () => handleDelete(p.row) },
        ]} />
      ),
    },
  ];

  return (
    <Box>
      <PageHeader breadcrumbs={["Catalog"]} title="Items"
        actions={<>{canCreate && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
            New Item
          </Button>
        )}</>}
      />

      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mb: 2 }}>
          <TextField
            placeholder="Search by SKU, name, or generic name"
            fullWidth size="small" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
          />
          <TextField select label="Status" size="small" value={status}
            onChange={(e) => setStatus(e.target.value as typeof status)}
            sx={{ minWidth: 150 }}>
            <MenuItem value="active">Active only</MenuItem>
            <MenuItem value="inactive">Inactive only</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        </Stack>
        {query.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(query.error)}</Alert>}
        {deleteMutation.isError && <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteMutation.error)}</Alert>}
        <Box sx={{ height: 600 }}>
          <DataGrid
            rows={query.data?.items ?? []}
            columns={columns}
            loading={query.isLoading}
            paginationMode="server"
            rowCount={query.data?.totalCount ?? 0}
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            getRowId={(row) => row.id}
            onRowDoubleClick={(p: GridRowParams<ItemDto>) => canUpdate && openEdit(p.row)}
          />
        </Box>
      </Paper>

      <ItemForm open={formOpen} onClose={() => setFormOpen(false)} item={editing} />
    </Box>
  );
}
